@file:Suppress("MagicNumber", "TooGenericExceptionCaught", "TooManyFunctions")

package com.nonopn.skydome.common.app.pages.probes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.UriHandler
import androidx.compose.ui.unit.dp
import com.nonopn.skydome.common.app.api.impl.AccountAction
import com.nonopn.skydome.common.app.api.impl.AccountCheckoutRequest
import com.nonopn.skydome.common.app.api.impl.AccountStatus
import com.nonopn.skydome.common.app.api.impl.ProbeApp
import com.nonopn.skydome.common.app.api.impl.ProbeToCreate
import com.nonopn.skydome.common.app.api.impl.ProbesCheckoutRequest
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.navigation.RouterProbes
import com.nonopn.skydome.common.app.pages.promocodes.PromoCheckResult
import com.nonopn.skydome.common.app.pages.promocodes.PromoCodeUiState
import com.nonopn.skydome.common.app.pages.promocodes.canApplyPromoCode
import com.nonopn.skydome.common.app.pages.promocodes.checkPromoCode
import com.nonopn.skydome.common.app.pages.promocodes.promoCheckoutFailure
import com.nonopn.skydome.common.app.pages.promocodes.promoSubscriptionRunning
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.probe_default_name
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.maps.MapUtils
import com.nonopn.skywatcher.maps.providers.GoogleProvider
import eu.codlab.viewmodel.StateViewModel
import korlibs.time.DateTime
import org.jetbrains.compose.resources.getString
import ovh.plrapps.mapcompose.api.addLayer
import ovh.plrapps.mapcompose.api.addMarker
import ovh.plrapps.mapcompose.api.addPath
import ovh.plrapps.mapcompose.api.maxScale
import ovh.plrapps.mapcompose.api.moveMarker
import ovh.plrapps.mapcompose.api.onTap
import ovh.plrapps.mapcompose.api.removeMarker
import ovh.plrapps.mapcompose.api.removePath
import ovh.plrapps.mapcompose.api.scale
import ovh.plrapps.mapcompose.api.snapScrollTo
import ovh.plrapps.mapcompose.core.TileStreamProvider
import ovh.plrapps.mapcompose.ui.state.MapState
import kotlin.math.pow

private const val LEVEL_COUNT = 18
private val FULL_SIZE = (256 * 2.0.pow(LEVEL_COUNT)).toInt()
private const val INITIAL_SCALE = 0.001
private const val MAX_SCALE = 3.0
private const val DEFAULT_LAT = 46.5
private const val DEFAULT_LON = 2.5

// A tap on a draft marker must not also place a new probe (the map tap can fire right after)
private const val MARKER_TAP_GUARD_MILLIS = 400L
private const val HIT_RADIUS_PX = 30.0
private const val RADIUS_METERS = PROBE_RADIUS_KM * 1000

private val ExistingProbeRed = Color(0xFFE63B2E)

data class ProbeCreationState(
    val trialMode: Boolean = false,
    val drafts: List<DraftProbe> = emptyList(),
    val existingCount: Int = 0,
    val submitting: Boolean = false,
    val error: ProbeCallError? = null,
    // What the account can do according to GET /api/account/status (null = route failed: previous behaviour)
    val actions: AccountActions? = null,
    // "Promo code" field (paid creation only), and whether a subscription is running (null = unknown)
    val promo: PromoCodeUiState = PromoCodeUiState(),
    val subscriptionRunning: Boolean? = null,
)

/** Map + state of the probe creation screen: new probes are placed, removed and dragged; existing ones are fixed. */
class ProbeCreationModel(
    private val appModel: AppModel,
    trialMode: Boolean,
) : StateViewModel<ProbeCreationState>(ProbeCreationState(trialMode = trialMode)) {
    private val googleApi = GoogleProvider()
    private val tileStreamProvider = TileStreamProvider { row, col, zoomLvl ->
        googleApi.getTile(row, col, zoomLvl)
    }
    private var accountStatus: AccountStatus? = null
    private var lastMarkerInteractionMillis = 0L
    private var idCounter = 0

    val mapState = MapState(
        levelCount = LEVEL_COUNT,
        fullWidth = FULL_SIZE,
        fullHeight = FULL_SIZE,
        tileSize = 256,
    ).apply {
        scale = INITIAL_SCALE
        maxScale = MAX_SCALE
        onTap { x, y -> onMapTap(x, y) }
    }

    init {
        safeLaunch {
            googleApi.initialize()
            mapState.addLayer(tileStreamProvider)
            // Existing probes: the status route first (every probe of the account), the reports as a fallback
            val status = appModel.loadAccountStatus()
            accountStatus = status
            val existing = status?.probes?.filterNot { isDeletedApiProbe(it) }
                ?.map { ExistingProbePoint(it.uuid, it.latitude, it.longitude) }
                ?: try {
                    appModel.cachedDashboardReports().map { it.probe }.filterNot { isProbeDeleted(it.status) }
                        .map { ExistingProbePoint(it.uuid, it.latitude, it.longitude) }
                } catch (_: Throwable) {
                    emptyList()
                }
            existing
                .filter { it.latitude != 0.0 && it.longitude != 0.0 }
                .forEach { drawExisting(it.uuid, it.latitude, it.longitude) }
            updateState {
                copy(
                    existingCount = existing.size,
                    actions = status?.accountActions(),
                    subscriptionRunning = status.promoSubscriptionRunning()
                )
            }
            val start = existing.firstOrNull { it.latitude != 0.0 }
                ?.let { LatLng(it.latitude, it.longitude) }
                ?: appModel.getSpecificData()?.coordinates?.takeIf { it.latitude != 0.0 }
                ?: LatLng(DEFAULT_LAT, DEFAULT_LON)
            val xy = MapUtils.latLngToXY(start)
            mapState.snapScrollTo(xy.x, xy.y)
        }
    }

    // ─── Map drawing ─────────────────────────────────────────────────────────

    private fun drawCircle(id: String, latitude: Double, longitude: Double, color: Color) {
        try {
            mapState.removePath(id)
        } catch (_: Throwable) {
        }
        mapState.addPath(
            id = id,
            width = 2.dp,
            color = color,
            fillColor = color.copy(alpha = 0.12f),
        ) {
            MapUtils.circle(LatLng(latitude, longitude), RADIUS_METERS) {
                MapUtils.latLngToXY(this).toPair()
            }?.let { list -> addPoints(list) }
        }
    }

    private fun drawExisting(uuid: String, latitude: Double, longitude: Double) {
        val xy = MapUtils.latLngToXY(latitude, longitude)
        mapState.addMarker("existing_$uuid", xy.x, xy.y, relativeOffset = Offset(-0.5f, -0.5f)) {
            ProbeDot(ExistingProbeRed, 16)
        }
        drawCircle("existing_circle_$uuid", latitude, longitude, ExistingProbeRed)
    }

    private fun drawDraft(draft: DraftProbe) {
        val xy = MapUtils.latLngToXY(draft.latitude, draft.longitude)
        val id = draft.id
        mapState.addMarker("draft_$id", xy.x, xy.y, relativeOffset = Offset(-0.5f, -0.5f)) {
            DraftMarker(
                onTap = { onDraftTap(id) },
                onDragBy = { dx, dy -> onDraftDrag(id, dx, dy) },
                onDragEnd = { lastMarkerInteractionMillis = nowMillis() }
            )
        }
        drawCircle("draft_circle_$id", draft.latitude, draft.longitude, ProbeOrange)
    }

    private fun eraseDraft(id: String) {
        try {
            mapState.removeMarker("draft_$id")
            mapState.removePath("draft_circle_$id")
        } catch (_: Throwable) {
        }
    }

    // ─── Interactions ────────────────────────────────────────────────────────

    private fun nowMillis() = DateTime.now().unixMillisLong

    private fun onMapTap(x: Double, y: Double) {
        if (states.value.submitting) return
        if (nowMillis() - lastMarkerInteractionMillis < MARKER_TAP_GUARD_MILLIS) return
        val nearDraft = states.value.drafts.any {
            val xy = MapUtils.latLngToXY(it.latitude, it.longitude)
            isNearOnScreen(x, y, xy.x, xy.y, HIT_RADIUS_PX, mapState.scale, FULL_SIZE.toDouble())
        }
        if (nearDraft) return
        val latLng = MapUtils.xyToLatLng(x, y)
        val old = states.value.drafts
        idCounter++
        val updated = ProbeDrafts.add(
            old,
            "d${nowMillis()}_$idCounter",
            latLng.latitude,
            latLng.longitude,
            states.value.trialMode
        )
        old.filter { o -> updated.none { it.id == o.id } }.forEach { eraseDraft(it.id) }
        updated.filter { n -> old.none { it.id == n.id } }.forEach { drawDraft(it) }
        updateState { copy(drafts = updated) }
    }

    fun onDraftTap(id: String) {
        lastMarkerInteractionMillis = nowMillis()
        removeDraft(id)
    }

    fun removeDraft(id: String) {
        eraseDraft(id)
        updateState { copy(drafts = ProbeDrafts.remove(drafts, id)) }
    }

    private fun onDraftDrag(id: String, dxPx: Float, dyPx: Float) {
        lastMarkerInteractionMillis = nowMillis()
        val draft = states.value.drafts.firstOrNull { it.id == id } ?: return
        if (!ProbeDrafts.canMove(states.value.drafts, id)) return
        val scale = mapState.scale
        val xy = MapUtils.latLngToXY(draft.latitude, draft.longitude)
        val full = FULL_SIZE.toDouble()
        val nx = (xy.x + dragDeltaNormalized(dxPx, scale, full)).coerceIn(0.0, 1.0)
        val ny = (xy.y + dragDeltaNormalized(dyPx, scale, full)).coerceIn(0.0, 1.0)
        val latLng = MapUtils.xyToLatLng(nx, ny)
        updateState { copy(drafts = ProbeDrafts.move(drafts, id, latLng.latitude, latLng.longitude)) }
        mapState.moveMarker("draft_$id", nx, ny)
        drawCircle("draft_circle_$id", latLng.latitude, latLng.longitude, ProbeOrange)
    }

    fun clearError() = updateState { copy(error = null) }

    // ─── Promo code ──────────────────────────────────────────────────────────

    fun onPromoInput(text: String) = updateState { copy(promo = promo.typing(text)) }

    fun removePromo() = updateState { copy(promo = promo.removed()) }

    /** "Apply": GET /api/checkout/promotion-codes/{code} with the bearer of the account. */
    fun applyPromo() {
        val input = states.value.promo.input
        if (!canApplyPromoCode(input, states.value.promo.checking)) return
        updateState { copy(promo = promo.startChecking()) }
        safeLaunch {
            when (val result = appModel.checkPromoCode(input)) {
                is PromoCheckResult.Accepted -> updateState { copy(promo = promo.accepted(result.code)) }
                is PromoCheckResult.Refused -> updateState { copy(promo = promo.refused(result.failure)) }
            }
        }
    }

    // ─── Server calls ────────────────────────────────────────────────────────

    private fun api() = appModel.states.value.apiProvider.let { it?.apiNormal ?: it?.apiAdmin }

    /** "Confirm and pay" (several probes) or "Start the trial" (single probe, isDemo). */
    fun submit(uriHandler: UriHandler) {
        val current = states.value
        if (current.submitting || current.drafts.isEmpty()) return
        val api = api()
        if (api == null) {
            updateState { copy(error = ProbeCallError(ProbeFailure.Generic)) }
            return
        }
        // The status route says the trial cannot be started: no call, the same message as the server would give
        if (current.trialMode && !current.actions.allows(AccountAction.StartDemo)) {
            updateState { copy(error = ProbeCallError(trialUnavailableFailure(accountStatus))) }
            return
        }
        val call = if (current.trialMode) ProbeCall.Demo else ProbeCall.Create
        updateState { copy(submitting = true, error = null) }
        safeLaunch {
            try {
                // The API requires a name: "Sonde 1", "Sonde 2"... (localized)
                val drafts = if (current.trialMode) current.drafts.take(1) else current.drafts
                val toCreate = ProbeDrafts.renumber(drafts).map {
                    ProbeToCreate(it.latitude, it.longitude, getString(Res.string.probe_default_name, it.number))
                }
                val answer = api.checkoutProbes(
                    ProbesCheckoutRequest(
                        app = ProbeApp.STRIKE_RADAR,
                        probes = toCreate,
                        isDemo = if (current.trialMode) true else null,
                        // Refused for a demo: only sent with a paid creation
                        promotionCode = if (current.trialMode) null else current.promo.codeToSend,
                    )
                )
                appModel.invalidateDashboardCache()
                updateState { copy(submitting = false) }
                val link = if (current.trialMode) answer.demoLink else answer.paymentLink
                when {
                    !link.isNullOrBlank() -> uriHandler.openUri(link)
                    current.trialMode -> appModel.show(RouterProbes.navigateTo())
                    // Paid automatically by the active subscription
                    else -> updateState { copy(error = ProbeCallError(ProbeFailure.PaidBySubscription)) }
                }
            } catch (e: Throwable) {
                // A refused promo code is shown under its field, the payment stays possible without it
                val promoFailure = if (current.trialMode) null else e.promoCheckoutFailure()
                if (promoFailure != null) {
                    updateState { copy(submitting = false, promo = promo.refused(promoFailure)) }
                } else {
                    val error = e.toProbeCallError(call)
                    updateState { copy(submitting = false, error = error) }
                }
            }
        }
    }

    /** Closes the outcome dialog; after an automatic payment the probes page is shown. */
    fun dismissError() {
        val paid = states.value.error?.failure == ProbeFailure.PaidBySubscription
        updateState { copy(error = null) }
        if (paid) appModel.show(RouterProbes.navigateTo())
    }

    /** "Pay for my probe" after the "trial unavailable" error: converts the account demo (checkout/upgrade). */
    fun payExistingTrial(uriHandler: UriHandler) {
        updateState { copy(error = null) }
        val api = api() ?: return
        safeLaunch {
            try {
                val answer = api.checkoutUpgrade(AccountCheckoutRequest(ProbeApp.STRIKE_RADAR))
                appModel.invalidateDashboardCache()
                val link = answer.paymentLink
                if (isPaidBySubscription(link)) {
                    updateState { copy(error = ProbeCallError(ProbeFailure.PaidBySubscription)) }
                } else {
                    uriHandler.openUri(link.orEmpty())
                }
            } catch (e: Throwable) {
                val error = e.toProbeCallError(ProbeCall.Upgrade)
                updateState { copy(error = error) }
            }
        }
    }
}

// ─── Markers ─────────────────────────────────────────────────────────────────

@Composable
internal fun ProbeDot(color: Color, sizeDp: Int) {
    Box(
        modifier = Modifier
            .size(sizeDp.dp)
            .background(color, CircleShape)
            .border(2.dp, Color.White, CircleShape)
    )
}

/** New probe: tap = remove, drag = move. Large transparent touch area around the dot. */
@Composable
private fun DraftMarker(onTap: () -> Unit, onDragBy: (Float, Float) -> Unit, onDragEnd: () -> Unit) {
    Box(
        modifier = Modifier
            .size(48.dp)
            .pointerInput(Unit) { detectTapGestures(onTap = { onTap() }) }
            .pointerInput(Unit) {
                detectDragGestures(
                    onDragEnd = { onDragEnd() },
                    onDragCancel = { onDragEnd() }
                ) { change, dragAmount ->
                    change.consume()
                    onDragBy(dragAmount.x, dragAmount.y)
                }
            },
        contentAlignment = Alignment.Center
    ) {
        ProbeDot(ProbeOrange, 24)
    }
}
